import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Play } from 'react-feather';
import {
  matchingSessionsApi,
  type MatchingReadiness,
} from '../../../../services/api/matchingSessions';
import { DisabledReasonTooltip } from './DisabledReasonTooltip';
import { deriveStartErrorMessage, isStartDisabled, startDisabledReasons } from './matchingStart';

interface StartMatchingButtonProps {
  cohortId: string;
  groupsPrereqsMet: boolean;
  /**
   * Pre-flight readiness from the backend. While loading this will be
   * `undefined` — the button stays disabled until we have an answer to avoid
   * letting the admin click before we know whether they should be able to.
   */
  readiness: MatchingReadiness | undefined;
}

/**
 * "Start Matching" primary button for the cohort Groups tab (MED-829 / E23-8 AC1-AC4).
 *
 * - Click → POST /api/cohorts/:cohortId/matching-sessions and navigate the
 *   matching_url in a new tab so the SGOS tab stays open as the admin's
 *   anchor back to the cohort. Falls back to in-tab nav if a popup blocker
 *   refuses the new-tab open. (Spec §5 said "no new tab opening required",
 *   meaning permitted but not mandated — we picked permitted to address the
 *   "no UI to come back to SGOS" UX gap surfaced post-implementation.)
 * - In-flight: button shows loading state and is disabled (AC2).
 * - On error: toast with retry messaging; do NOT navigate (AC3); orphan tab
 *   is closed.
 * - Disabled when groupsPrereqsMet is false (AC4 — same blocking conditions
 *   as the legacy Upload Matched Groups button). The "Edit Cohort Details"
 *   CTA is rendered by the parent's existing prereqs Alert.
 */
export function StartMatchingButton({
  cohortId,
  groupsPrereqsMet,
  readiness,
}: StartMatchingButtonProps) {
  const [isStarting, setIsStarting] = useState(false);

  // Disable when (a) cohort-level prereqs (session dates / time slots) aren't
  // met, (b) readiness is still loading, or (c) backend reports blockers.
  const isDisabled = isStartDisabled(groupsPrereqsMet, readiness);
  const tooltipReasons = isDisabled ? startDisabledReasons(groupsPrereqsMet, readiness) : [];

  const handleClick = async () => {
    // Open a placeholder tab synchronously so the browser counts it as a
    // user-initiated open and doesn't block the popup. Severing `opener`
    // immediately blocks reverse-tabnabbing before we navigate cross-origin.
    const newTab = window.open('about:blank', '_blank');
    if (newTab) newTab.opener = null;

    setIsStarting(true);
    try {
      const response = await matchingSessionsApi.start(cohortId);
      if (newTab && !newTab.closed) {
        newTab.location.href = response.matching_url;
      } else {
        // Popup blocked OR user closed the placeholder tab before we resolved.
        // Fall back to in-tab navigation per spec §5.
        window.location.href = response.matching_url;
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      notifications.show({
        color: 'red',
        title: "Couldn't start matching",
        message: deriveStartErrorMessage(err),
      });
      setIsStarting(false);
    }
  };

  const button = (
    <Button
      leftSection={<Play size={18} />}
      onClick={handleClick}
      loading={isStarting}
      disabled={isDisabled}
    >
      Start Matching
    </Button>
  );

  return <DisabledReasonTooltip reasons={tooltipReasons}>{button}</DisabledReasonTooltip>;
}
